import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const NewsSection = () => {
  const { t, isRTL } = useLanguage();

  return (
    <section className="py-24 bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.news.title}</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {t.news.description}
          </p>
        </motion.div>

        {/* News Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {t.newsItems.map((news, index) => (
            <motion.article
              key={news.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group"
            >
              <Link to={`/news/${news.id}`} className="block">
                <div className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
                  <div className={`flex flex-col md:flex-row ${isRTL ? 'md:flex-row-reverse' : ''}`}>
                    {/* Image */}
                    <div className="md:w-2/5 relative overflow-hidden">
                      <img
                        src={news.image}
                        alt={news.title}
                        className="w-full h-48 md:h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>

                    {/* Content */}
                    <div className="md:w-3/5 p-6">
                      <div className={`flex items-center gap-4 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <span className="px-3 py-1 bg-[#9B4B8C]/10 text-[#9B4B8C] text-xs font-medium rounded-full">
                          {news.category}
                        </span>
                        <div className={`flex items-center text-gray-500 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <Calendar className={`w-4 h-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                          {news.date}
                        </div>
                      </div>

                      <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-[#9B4B8C] transition-colors">
                        {news.title}
                      </h3>

                      <p className="text-gray-600 text-sm leading-relaxed mb-4">
                        {news.excerpt}
                      </p>

                      <div className={`flex items-center text-[#9B4B8C] text-sm font-medium ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <span>{t.news.readMore}</span>
                        <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'} transform group-hover:translate-x-1 transition-transform`} />
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NewsSection;
